<template>
  <Default>
    <BreadCrumb :items="breadcrumbs" />

    <div class="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-8">
      <!-- Page Header -->
      <div class="tw-text-center tw-mb-8">
        <h1 class="tw-text-3xl tw-font-bold c-blue-text tw-mb-2">Review Your Travel Insurance Quote</h1>
        <p class="tw-text-gray-600">Please review your coverage details and benefits before proceeding to payment</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="tw-text-center tw-py-12">
        <v-progress-circular size="64" color="primary" indeterminate></v-progress-circular>
        <p class="tw-mt-4 tw-text-gray-600">Loading your quote details...</p>
      </div>

      <!-- Quote Summary Component -->
      <div v-else-if="quote && quote.id && quote.premium && quote.destination && quote.travel_type">
        <TravelQuoteSummary
          :show-actions="true"
          :standalone="true"
          @proceed-to-payment="proceedToPayment"
          @edit-quote="editQuote"
        />
      </div>

      <!-- Error State -->
      <div v-else class="tw-text-center tw-py-12">
        <v-icon size="64" color="error" class="tw-mb-4">mdi-alert-circle</v-icon>
        <h2 class="tw-text-xl tw-font-semibold tw-text-red-600 tw-mb-2">Quote Not Found</h2>
        <p class="tw-text-gray-600 tw-mb-6">We couldn't find your travel insurance quote. Please try creating a new quote.</p>
        <v-btn color="primary" @click="createNewQuote">
          <v-icon start>mdi-plus</v-icon>
          Create New Quote
        </v-btn>
      </div>
    </div>
  </Default>
</template>

<script>
import Default from '@/components/layout/Default';
import TravelQuoteSummary from '@/components/rfq/travel/TravelQuoteSummary';
import { mapGetters } from 'vuex';
import BreadCrumb from '@/components/dashboard/BreadCrumb';
import exchangeRates from '@/config/exchangeRates';
import axios from 'axios';
import { travelPaymentReference } from '@/utils/travelPaymentReference';

export default {
  name: 'TravelQuoteSummaryView',
  components: {
    Default,
    BreadCrumb,
    TravelQuoteSummary,
  },
  data() {
    return {
      loading: true,
      breadcrumbs: [
        {
          text: 'Travel Insurance',
          disabled: false,
          to: { name: 'Request', query: { b: 'travel' } },
        },
        {
          text: 'Quote Summary',
          disabled: true,
          to: { name: 'TravelQuoteSummary' },
        },
      ],
    };
  },
  computed: {
    ...mapGetters('rfq', ['getTravelQuoteResponse']),
    quote() {
      return this.getTravelQuoteResponse;
    },
    payableAmountKsh() {
      return this.quote?.premium ? exchangeRates.convertUsdToKsh(this.quote.premium) : 0;
    },
  },
  async mounted() {
    // console.log('TravelQuoteSummary mounted, route query:', this.$route.query);
    // console.log('Quote from store:', this.quote);
    // console.log('Quote keys:', this.quote ? Object.keys(this.quote) : 'null');
    // console.log('Quote data details:', {
    //   id: this.quote?.id,
    //   destination: this.quote?.destination,
    //   region: this.quote?.region,
    //   travel_type: this.quote?.travel_type,
    //   plan_type: this.quote?.plan_type,
    //   start_date: this.quote?.start_date,
    //   end_date: this.quote?.end_date,
    //   duration: this.quote?.duration,
    //   number_of_travelers: this.quote?.number_of_travelers,
    //   coverage_amount: this.quote?.coverage_amount,
    //   premium: this.quote?.premium,
    // });

    try {
      // Check if we have valid quote data from the store - need essential display fields
      const hasValidQuote = this.quote
        && this.quote.id
        && this.quote.premium
        && this.quote.destination
        && this.quote.travel_type;

      if (!hasValidQuote && this.$route.query.q) {
        // console.log('No valid quote in store, trying to fetch by ID:', this.$route.query.q);
        // If no valid quote in store but we have query param, try to fetch it
        await this.fetchQuoteById();
      } else if (!hasValidQuote) {
        // console.log('No quote data available, redirecting to create new quote');
        // No quote data available, redirect to create new quote
        this.$toast.error('No quote data found. Please create a new quote.', 'Error');
        this.$router.push({ name: 'Request', query: { b: 'travel' } });
        return;
      } else {
        // console.log('Valid quote data found in store with all required fields');
        // We have valid quote data, ensure it's stored in Vuex for consistency
        this.$store.commit('rfq/setTravelQuoteResponse', this.quote);
      }
    } catch (error) {
      // console.error('Error loading quote:', error);
      this.$toast.error('Failed to load quote details', 'Error');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchQuoteById() {
      // console.log('Fetching quote by ID:', this.$route.query.q);
      const response = await axios.get(`/customer/digital-quotation/pacis-travel/quotes/${this.$route.query.q}`);
      // console.log('Fetched quote from API:', response.data);
      // console.log('Fetched quote fields:', response.data ? Object.keys(response.data) : 'no data');
      // console.log('Quote validation check:', {
      //   'quote.id': response.data?.id,
      //   'quote.premium': response.data?.premium,
      //   'quote.destination': response.data?.destination,
      //   'quote.travel_type': response.data?.travel_type,
      //   'quote.start_date': response.data?.start_date,
      //   'quote.end_date': response.data?.end_date,
      //   'quote.coverage_amount': response.data?.coverage_amount,
      // });

      // Map and enhance quote data to match component expectations (same as resumeTravelQuote)
      const mappedQuote = {
        ...response.data,
        // Map fields for TravelQuoteSummary component
        destination: response.data.destination || response.data.region || 'Unknown Destination',
        travel_type: response.data.travel_type || response.data.plan_type || 'Unknonw Type',
        start_date: response.data.start_date || response.data.travel_start_date || 'TBD',
        end_date: response.data.end_date || response.data.travel_end_date || 'TBD',
        number_of_travelers: response.data.number_of_travelers || response.data.travelers || 1,

        // Ensure coverage_amount has a proper value based on plan type and region
        coverage_amount: this.getCoverageAmountByRegion(response.data.region || response.data.destination, response.data.plan_type || response.data.travel_type),

        // Ensure other required fields
        premium: response.data.premium || response.data.revised_price || 0,
        region: response.data.region || response.data.destination || 'Unknown',
        plan_type: response.data.plan_type || response.data.travel_type || 'Unknown',
      };

      // console.log('Mapped quote data:', mappedQuote);

      // Update the store with the mapped quote data
      this.$store.commit('rfq/setTravelQuoteResponse', mappedQuote);
      this.loading = false;
    },
    proceedToPayment() {
      // Set up payment context and navigate to personal details / payment
      const paymentContext = {
        payableAmount: this.payableAmountKsh, // Convert USD to KSH
        product: 'Travel Insurance',
        productType: 'travel',
        productId: this.quote.id,
        email: this.quote.email_address,
        phoneNumber: this.quote.phone_number,
        transactionReference: travelPaymentReference(this.quote?.id_no || this.quote?.id_number || this.quote?.passport_no || this.quote?.passport),
        transactionDescription: `TV_rfq-${this.quote.id}`,
        quoteData: this.quote,
      };

      // console.log('Setting payment context from quote summary:', paymentContext);
      this.$store.commit('payments/setPaymentContext', paymentContext);

      // Navigate to personal details for travel insurance
      this.$router.push({
        name: 'BuyCover',
        query: { q: this.quote.id, b: 'travel', fromQuoteSummary: 'true' },
      });
    },
    editQuote() {
      // Navigate back to quote creation to edit
      this.$router.push({
        name: 'Request',
        query: { b: 'travel', edit: this.quote.id },
      });
    },
    createNewQuote() {
      // Navigate to create new travel quote
      this.$router.push({
        name: 'Request',
        query: { b: 'travel' },
      });
    },
    getCoverageAmountByRegion(region, planType) {
      // Based on PACIS INSURANCE TRAVEL PROTECT coverage schedule
      const coverageMap = {
        'Africa/Asia': 15000,
        Europe: 40000,
        'Worldwide Basic': 40000,
        'Worldwide Plus': 75000,
        'Worldwide Extra': 150000,
        'Worldwide Classic': 60000, // Student plan
        'Worldwide Premium': 100000, // Student premium plan
      };

      // Handle student plans
      if (planType === 'student') {
        if (region && region.toLowerCase().includes('premium')) {
          return 100000;
        }
        return 60000; // Default student coverage
      }

      // Match region to coverage amount
      const normalizedRegion = region || 'Africa/Asia';

      // Try exact match first
      if (coverageMap[normalizedRegion]) {
        return coverageMap[normalizedRegion];
      }

      // Try partial matching for common variations
      const regionLower = normalizedRegion.toLowerCase();
      if (regionLower.includes('africa') || regionLower.includes('asia')) {
        return 15000;
      }
      if (regionLower.includes('europe')) {
        return 40000;
      }
      if (regionLower.includes('worldwide') && regionLower.includes('extra')) {
        return 150000;
      }
      if (regionLower.includes('worldwide') && regionLower.includes('plus')) {
        return 75000;
      }
      if (regionLower.includes('worldwide')) {
        return 40000; // Default worldwide basic
      }

      // Default fallback
      return 15000;
    },
  },
};
</script>
